import React, { useMemo } from 'react';
import { TreeSelect } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { trpc } from '../../lib/api';

interface LocationTreeSelectProps {
  value?: string;
  onChange?: (value: string | undefined) => void;
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
}

/**
 * エリアツリーセレクトコンポーネント
 * 拠点配下のエリアを階層構造で表示・選択できます
 */
export const LocationTreeSelect: React.FC<LocationTreeSelectProps> = ({
  value,
  onChange,
  placeholder = 'エリアを選択',
  allowClear = true,
  disabled = false,
}) => {
  // 拠点一覧取得
  const { data: branches = [], isLoading: branchesLoading } = trpc.branch.list.useQuery(
    {},
    {
      staleTime: 5 * 60 * 1000, // 5分間キャッシュ
    }
  );

  // エリア一覧取得（全拠点）
  const { data: locations = [], isLoading: locationsLoading } = trpc.location.getList.useQuery(
    {},
    {
      staleTime: 5 * 60 * 1000, // 5分間キャッシュ
    }
  );

  const isLoading = branchesLoading || locationsLoading;

  // ツリーデータ構築
  const treeData = useMemo(() => {
    const tree: DefaultOptionType[] = [];

    // 拠点ごとにグループ化
    branches.forEach(branch => {
      // この拠点のエリアのみフィルタ
      const branchLocations = locations.filter(loc => loc.branchId === branch.id);

      if (branchLocations.length === 0) {
        // エリアがない拠点は表示しない
        return;
      }

      // 再帰的にツリー構築
      const buildTree = (parentId: string | null): DefaultOptionType[] => {
        return branchLocations
          .filter(loc => loc.parentId === parentId)
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          .map(loc => ({
            title: loc.name,
            value: loc.id,
            children: buildTree(loc.id),
          }));
      };

      // 親エリアがnullのものをルートとする
      const rootLocations = buildTree(null);

      // 拠点ノード作成（選択不可）
      tree.push({
        title: branch.name,
        value: `branch-${branch.id}`, // 拠点自体は選択不可
        disabled: true,
        selectable: false,
        children: rootLocations,
      });
    });

    return tree;
  }, [branches, locations]);

  return (
    <TreeSelect
      value={value}
      onChange={onChange}
      treeData={treeData}
      placeholder={placeholder}
      allowClear={allowClear}
      disabled={disabled || isLoading}
      loading={isLoading}
      showSearch
      treeDefaultExpandAll={false}
      filterTreeNode={(input, node) =>
        (node.title as string)?.toLowerCase().includes(input.toLowerCase())
      }
      style={{ width: '100%' }}
    />
  );
};
